<script setup lang="ts">
    import { getConfig, klinikenApi } from "@/api";
    import InfoIcon from "@/assets/info_dark.svg";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { Trigger } from "@/components/forms/Form/types";
    import { Option } from "@/components/forms/inputs/Autocomplete/types";
    import { useStore } from "@/store";
    import { openNLLErrorDialog } from "@/tabs/Receptforskrivning/helpers";
    import moment from "moment/moment";
    import { toRefs } from "vue";

    const store = useStore();

    const props = defineProps<{
        forskrivningId?: string;
        parallellId?: string;
        senasteDatumForAvslutAvBehandling: string | null;
    }>();

    const { forskrivningId, parallellId, senasteDatumForAvslutAvBehandling } = toRefs(props);

    const emit = defineEmits(["submit", "close"]);

    const triggers: Trigger[] = [
        {
            fieldName: "beskrivning_orsak",
            dependentOn: "orsak",
            action: ({ dependentOnValue }) => {
                return {
                    required: (dependentOnValue as string) === "OTH",
                };
            },
        },
    ];

    const close = () => {
        emit("close");
    };

    const submit = async (data: any) => {
        emit("submit", {
            data,
            forskrivning_id: forskrivningId?.value,
            parallell_id: parallellId?.value,
            typ: "avsluta",
        });
    };

    const getEndReasonOptions = async () => {
        try {
            const response = await klinikenApi.get(
                "/nll/kodverk/handelseskal/",
                getConfig({ headers: store.getters["nll/headers"] })
            );
            return response.data.map((reason: any) => {
                return {
                    value: reason.code,
                    label: reason.display,
                };
            });
        } catch (error: any) {
            openNLLErrorDialog({ error, description: "Det gick inte att hämta orsaker till avslut." });

            return [];
        }
    };

    const getToday = () => moment().format("yyyy-MM-DD");

    const getTodayLabel = () => `${getToday()}, idag`;

    const getDeleteOption: () => Option = () => ({ value: "delete", label: "Inget datum" });

    const getAvslutAvBehandlingOptions = () => {
        const options: Option[] = [];

        if (senasteDatumForAvslutAvBehandling?.value) {
            const dateIsValid = moment(senasteDatumForAvslutAvBehandling.value).isSameOrAfter(moment(), "day");

            options.push({
                value: "retain",
                label: `
                        ${moment(senasteDatumForAvslutAvBehandling.value).format("yyyy-MM-DD")},
                        tidigare angivet${dateIsValid ? "" : ". Kan bara väljas om det är dagens datum eller senare"}
                    `,
                disabled: !dateIsValid,
            });
        }

        options.push({ value: "replace", label: getTodayLabel() }, getDeleteOption());

        return options;
    };

    const getDoseringOptions = () => {
        const options: Option[] = [
            {
                value: "replace",
                label: getTodayLabel(),
            },
            getDeleteOption(),
        ];

        return options;
    };
</script>

<template>
    <KForm
        id="avsluta-forskrivning"
        class="avsluta-forskrivning"
        submit-content="Avsluta förskrivning"
        submit-variant="alert"
        @submit="submit"
        cancel-content="Avbryt"
        @cancel="close"
        :triggers="triggers"
    >
        <div class="form-content">
            <div class="intro-info span-all">
                <img :src="InfoIcon" class="intro-info-icon" alt="Info" />
                <div class="intro-info-text">
                    <p>Observera att efter avslut kan förskrivningen inte längre expedieras på apotek.</p>
                    <br />
                    <p>Om du vill ta bort förskrivningen på grund av en felregistrering, välj istället makulera.</p>
                    <p v-if="parallellId">
                        <br />
                        Om du avslutar en förskrivning som är parallell, avslutas även den andra automatiskt.
                    </p>
                </div>
            </div>
            <KGroup name="datum_for_avslut" label="Datum för avslut" class="span-all">
                <KFormFieldDisplay
                    name="senaste_giltighetsdag"
                    :value="getToday()"
                    label="Senaste giltighetsdag"
                    align="center"
                />
                <KAutocomplete
                    name="sista_doseringsdag"
                    label="Sista doseringsdag"
                    :options="getDoseringOptions"
                    validation="required"
                />
                <KAutocomplete
                    name="senaste_datum_for_avslut_av_behandling"
                    label="Senaste datum för avslut av behandling"
                    :options="getAvslutAvBehandlingOptions"
                    validation="required"
                />
            </KGroup>
            <KGroup name="orsak_till_avslut" label="Orsak till avslut" class="span-all">
                <KAutocomplete
                    name="orsak"
                    label="Orsak till avslut"
                    :options="getEndReasonOptions"
                    class="span-all"
                    validation="required"
                />
                <KText name="beskrivning_orsak" label="Beskrivning av orsak" class="span-all" />
            </KGroup>
        </div>
    </KForm>
</template>

<style lang="scss">
    .avsluta-forskrivning {
        .intro-info {
            display: flex;
            align-items: start;
            gap: 0.5rem;

            img {
                margin-top: 0.2rem;
            }

            .intro-info-text {
                display: flex;
                flex-direction: column;
            }
        }

        .end-type-label {
            font-weight: bold;
            margin: 0;
        }

        ul {
            padding-left: 1.25rem;

            .end-type-info {
                list-style-type: none;
                position: relative;
            }

            .end-type-info::before {
                content: "•";
                position: absolute;
                left: -0.8rem;
            }
        }
    }
</style>
